/* Rich text stays editable; only citation text nodes become reference buttons. */
if (!customElements.get('sa-academic-quote')) {
  customElements.define('sa-academic-quote', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.dialog = this.querySelector('[data-reference-dialog]');
      this.previewPanel = this.querySelector('[data-reference-popover]');
      this.references = new Map();
      const duplicates = new Set();
      this.querySelectorAll('[data-reference]').forEach((reference) => {
        const number = reference.dataset.reference;
        if (this.references.has(number)) duplicates.add(number);
        else this.references.set(number, reference.querySelector('[data-reference-content]'));
      });
      // Leave the readable reference list in place if native dialogs are unavailable.
      if (!this.dialog?.showModal || !this.previewPanel) return;
      const missing = this.enhanceQuote();
      this.dataset.enhanced = '';
      const warning = this.querySelector('[data-reference-warning]');
      if (warning) {
        const messages = [];
        if (missing.size) messages.push(`Add a Study reference block for: ${[...missing].join(', ')}.`);
        if (duplicates.size) messages.push(`Use a different number for duplicate blocks: ${[...duplicates].join(', ')}.`);
        warning.textContent = messages.join(' ');
        warning.hidden = !messages.length;
      }
      this.addEventListener('click', (event) => this.handleClick(event), { signal });
      this.addEventListener('pointerover', (event) => {
        const marker = event.target.closest('[data-citation]');
        if (!marker || marker === this.suppressedMarker || marker.contains(event.relatedTarget) || event.pointerType !== 'mouse' || this.dialog.open) return;
        clearTimeout(this.closeTimer);
        clearTimeout(this.openTimer);
        this.openTimer = setTimeout(() => this.showPreview(marker), 160);
      }, { signal });
      this.addEventListener('pointerout', (event) => {
        const marker = event.target.closest('[data-citation]');
        if (!marker || marker.contains(event.relatedTarget)) return;
        if (marker === this.suppressedMarker && !this.dialog.open) this.suppressedMarker = null;
        clearTimeout(this.openTimer);
        this.schedulePreviewClose();
      }, { signal });
      this.previewPanel.addEventListener('pointerenter', () => clearTimeout(this.closeTimer), { signal });
      this.previewPanel.addEventListener('pointerleave', () => this.schedulePreviewClose(), { signal });
      this.previewPanel.addEventListener('focusin', () => clearTimeout(this.closeTimer), { signal });
      this.previewPanel.addEventListener('focusout', (event) => {
        if (!this.previewPanel.contains(event.relatedTarget)) this.schedulePreviewClose();
      }, { signal });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.suppressedMarker = this.previewMarker || this.opener;
          this.hidePreview();
        }
      }, { signal });
      document.addEventListener('pointerdown', (event) => {
        if (!this.previewPanel.contains(event.target) && !event.target.closest('[data-citation]')) this.hidePreview();
      }, { signal });
      window.addEventListener('resize', () => this.hidePreview(), { signal });
      document.addEventListener('scroll', (event) => {
        if (!this.previewPanel.contains(event.target)) this.hidePreview();
      }, { signal, capture: true, passive: true });
      this.dialog.addEventListener('pointerdown', (event) => {
        this.backdropDown = event.target === this.dialog && this.outsideDialog(event);
      }, { signal });
      this.dialog.addEventListener('keydown', (event) => this.trapFocus(event), { signal });
      this.dialog.addEventListener('close', () => {
        if (this.dialog.open) return;
        this.updateScrollLock();
        this.suppressedMarker = this.opener;
        if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      }, { signal });
      // Selecting a reference block in the theme editor previews that exact source.
      this.addEventListener('shopify:block:select', (event) => {
        const reference = event.target.closest('[data-reference]');
        if (reference) this.showReference(reference.dataset.reference);
      }, { signal });
      this.addEventListener('shopify:block:deselect', () => this.dialog.close(), { signal });
    }

    disconnectedCallback() {
      this.hidePreview();
      this.controller?.abort();
      this.controller = null;
      if (this.dialog?.open) this.dialog.close();
      this.updateScrollLock();
    }

    enhanceQuote() {
      const quote = this.querySelector('[data-quote-text]');
      const missing = new Set();
      if (!quote) return missing;
      const walker = document.createTreeWalker(quote, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => node.parentElement.closest('a, button, script, style, code') ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT
      });
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      const superscripts = { '¹': '1', '²': '2', '³': '3' };
      nodes.forEach((node) => {
        const matches = [...node.textContent.matchAll(/\[(\d{1,2})\]|[¹²³]/g)];
        if (!matches.length) return;
        const fragment = document.createDocumentFragment();
        let start = 0;
        matches.forEach((match) => {
          fragment.append(node.textContent.slice(start, match.index));
          const number = match[1] || superscripts[match[0]];
          const source = this.references.get(number);
          if (source) {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'sa-academic-quote__marker';
            button.dataset.citation = number;
            button.textContent = number;
            button.setAttribute('aria-haspopup', 'dialog');
            button.setAttribute('aria-controls', this.dialog.id);
            button.setAttribute('aria-label', source.querySelector('.sa-academic-quote__number').textContent.trim());
            fragment.append(button);
          } else {
            missing.add(number);
            fragment.append(match[0]);
          }
          start = match.index + match[0].length;
        });
        fragment.append(node.textContent.slice(start));
        node.replaceWith(fragment);
      });
      return missing;
    }

    handleClick(event) {
      const marker = event.target.closest('[data-citation]');
      if (marker) { this.showReference(marker.dataset.citation, marker); return; }
      if (event.target.closest('[data-close-reference]')) { this.dialog.close(); return; }
      if (event.target.closest('[data-close-popover]')) { this.hidePreview(); return; }
      if (event.target === this.dialog && this.backdropDown && this.outsideDialog(event)) this.dialog.close();
      this.backdropDown = false;
    }

    showReference(number, opener = null) {
      const source = this.references.get(number);
      if (!source) return;
      this.hidePreview();
      this.opener = opener;
      this.suppressedMarker = opener;
      const copy = source.cloneNode(true);
      copy.querySelector('[data-study-title]').id = this.dialog.getAttribute('aria-labelledby');
      this.dialog.querySelector('[data-dialog-content]').replaceChildren(copy);
      if (!this.dialog.open) this.dialog.showModal();
      this.dialog.scrollTop = 0;
      this.updateScrollLock();
      this.dialog.querySelector('[data-close-reference]').focus({ preventScroll: true });
    }

    showPreview(marker) {
      if (!marker.isConnected || this.dialog.open) return;
      this.hidePreview();
      this.previewMarker = marker;
      this.previewPanel.querySelector('[data-popover-content]').replaceChildren(this.references.get(marker.dataset.citation).cloneNode(true));
      this.previewPanel.setAttribute('aria-label', marker.getAttribute('aria-label'));
      this.previewPanel.hidden = false;
      if (this.previewPanel.showPopover) this.previewPanel.showPopover();
      else this.previewPanel.removeAttribute('popover');
      const rect = marker.getBoundingClientRect();
      const panel = this.previewPanel.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight;
      const left = Math.max(12, Math.min(rect.left + rect.width / 2 - panel.width / 2, viewportWidth - panel.width - 12));
      const top = rect.bottom + panel.height + 8 <= viewportHeight - 12 ? rect.bottom + 8 : Math.max(12, rect.top - panel.height - 8);
      this.previewPanel.style.left = `${left}px`;
      this.previewPanel.style.top = `${top}px`;
    }

    schedulePreviewClose() {
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => {
        if (!this.previewPanel.contains(document.activeElement)) this.hidePreview();
      }, 220);
    }

    hidePreview() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.previewMarker = null;
      if (!this.previewPanel) return;
      if (this.previewPanel.hidePopover && this.previewPanel.matches(':popover-open')) this.previewPanel.hidePopover();
      this.previewPanel.hidden = true;
    }

    outsideDialog(event) {
      const rect = this.dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    }

    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const focusable = [...this.dialog.querySelectorAll('button, a[href], [tabindex]')]
        .filter((element) => element.tabIndex >= 0 && !element.disabled && element.getClientRects().length);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    updateScrollLock() {
      document.documentElement.classList.toggle('sa-academic-reference-open', !!document.querySelector('sa-academic-quote dialog[open]'));
    }
  });
}
